
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth";
import { Product } from "@/lib/types";
import { addProductToApi } from "@/lib/storage/product/api";

// Use this instead of generateId() to create proper UUIDs
const generateUUID = (): string => {
  return crypto.randomUUID();
};

export const useProductForm = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<"ostomy" | "medicine" | "other">("ostomy");
  const [currentStock, setCurrentStock] = useState(0);
  const [usageRate, setUsageRate] = useState(1);
  const [minStockAlert, setMinStockAlert] = useState(7);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [packageSize, setPackageSize] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supplierName, setSupplierName] = useState("");
  const [supplierWebsite, setSupplierWebsite] = useState("");
  const [supplierContact, setSupplierContact] = useState("");
  const [productCode, setProductCode] = useState("");
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const validateForm = () => {
    if (!name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for the product",
        variant: "destructive",
      });
      return false;
    }

    if (currentStock < 0) {
      toast({
        title: "Invalid stock",
        description: "Current stock cannot be negative",
        variant: "destructive",
      });
      return false;
    }

    if (usageRate <= 0) {
      toast({
        title: "Invalid usage rate",
        description: "Usage rate must be greater than zero",
        variant: "destructive",
      });
      return false;
    }

    // Check if user is authenticated
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to add products",
        variant: "destructive",
      });
      navigate("/auth");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    console.log("handleSubmit called");
    
    // Validation
    if (!validateForm()) return;

    // Prevent double submission
    if (isSubmitting) {
      console.log("Already submitting, ignoring duplicate call");
      return;
    }
    
    setIsSubmitting(true);

    try {
      console.log("Creating new product object...");
      
      // Create new product - using proper UUID
      const newProduct: Product = {
        id: generateUUID(),
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        currentStock,
        usageRate,
        minStockAlert,
        groupIds: selectedGroups,
        createdAt: new Date(),
        updatedAt: new Date(),
        packageSize: packageSize > 0 ? packageSize : undefined,
        supplierName: supplierName.trim() || undefined,
        supplierWebsite: supplierWebsite.trim() || undefined,
        supplierContact: supplierContact.trim() || undefined,
        productCode: productCode.trim() || undefined,
      };

      console.log("Calling addProductToApi with product:", newProduct);
      
      // Add product to storage - use API function directly
      const addedProduct = await addProductToApi(newProduct);
      console.log("Product added successfully via API:", addedProduct);

      // Show success toast
      toast({
        title: "Product added",
        description: `${name} has been added to your inventory`,
      });

      // Navigate back to products page
      navigate("/products");
      
      return addedProduct;
    } catch (error) {
      console.error("Error adding product:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add product. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      throw error; // Re-throw so the component can handle it
    }
  };

  return {
    formState: {
      name,
      description,
      category,
      currentStock,
      usageRate,
      minStockAlert,
      selectedGroups,
      packageSize,
      supplierName,
      supplierWebsite,
      supplierContact,
      productCode,
      isSubmitting
    },
    setters: {
      setName,
      setDescription,
      setCategory,
      setCurrentStock,
      setUsageRate,
      setMinStockAlert,
      setPackageSize,
      setSupplierName,
      setSupplierWebsite,
      setSupplierContact,
      setProductCode
    },
    handleGroupToggle,
    handleSubmit,
    isSubmitDisabled: !name || isSubmitting
  };
};
